import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Clock, TrendingUp, CheckCircle, AlertCircle, Calendar, ChevronLeft, ChevronRight, Timer, Zap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

import API_URL from '../utils/api';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AttendanceAnalytics = () => {
    const { user, canManage } = useAuth();
    const [data, setData] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [loading, setLoading] = useState(true);

    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();

    useEffect(() => { fetchData(); }, [month, year, selectedEmployee]);

    useEffect(() => {
        if (canManage) {
            axios.get(`${API_URL}/api/employees`, { withCredentials: true }).then(r => setEmployees(r.data.filter(e => e.role !== 'admin'))).catch(() => {});
        }
    }, [canManage]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = `month=${month}&year=${year}${selectedEmployee ? `&employee_id=${selectedEmployee}` : ''}`;
            const { data: d } = await axios.get(`${API_URL}/api/attendance/analytics?${params}`, { withCredentials: true });
            setData(d);
        } catch (error) { logger.error(error); }
        finally { setLoading(false); }
    };

    const prevMonth = () => setCurrentDate(new Date(year, month - 2, 1));
    const nextMonth = () => setCurrentDate(new Date(year, month, 1));

    if (loading && !data) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading analytics...</div></div>;
    if (!data) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">No data available</div></div>;

    const s = data.summary;
    const daysInMonth = data.days_in_month;
    const firstDay = new Date(year, month - 1, 1).getDay();

    const getCellColor = (dayData) => {
        if (!dayData) return 'bg-slate-100';
        if (dayData.status === 'leave') return 'bg-purple-200';
        if (dayData.status === 'present') {
            if (dayData.is_late) return 'bg-amber-200';
            return 'bg-green-200';
        }
        return 'bg-red-100';
    };

    const getCellTooltip = (dayData, day) => {
        if (!dayData) return `Day ${day}: No record`;
        if (dayData.status === 'leave') return `Day ${day}: On Leave (${dayData.leave_type || ''})`;
        if (dayData.status === 'present') {
            let tip = `Day ${day}: Present (${dayData.hours}h)`;
            if (dayData.is_late) tip += ' - LATE';
            return tip;
        }
        return `Day ${day}: Absent`;
    };

    const pieData = [
        { name: 'Present', value: s.days_present, fill: '#22c55e' },
        { name: 'Absent', value: Math.max(0, s.days_absent), fill: '#ef4444' },
        { name: 'On Leave', value: s.days_on_leave, fill: '#a855f7' },
    ].filter(d => d.value > 0);

    const heatmapCells = [];
    for (let i = 0; i < firstDay; i++) heatmapCells.push(null);
    for (let d = 1; d <= daysInMonth; d++) heatmapCells.push(d);

    return (
        <div className="space-y-6" data-testid="attendance-analytics-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Attendance Analytics</h1>
                    <p className="text-slate-500 mt-1">{data.employee_name} {data.department ? `- ${data.department}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                    {canManage && (
                        <Select value={selectedEmployee || "self"} onValueChange={(v) => setSelectedEmployee(v === "self" ? "" : v)}>
                            <SelectTrigger className="w-[180px] rounded-none" data-testid="employee-filter"><SelectValue placeholder="My Analytics" /></SelectTrigger>
                            <SelectContent className="rounded-none">
                                <SelectItem value="self">My Analytics</SelectItem>
                                {employees.map(e => <SelectItem key={e._id} value={e._id}>{e.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                    <div className="flex items-center border border-slate-200">
                        <Button variant="ghost" size="sm" className="rounded-none h-9" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="h-4 w-4" /></Button>
                        <span className="px-3 text-sm font-medium text-slate-700 min-w-[130px] text-center">{MONTHS[month - 1]} {year}</span>
                        <Button variant="ghost" size="sm" className="rounded-none h-9" onClick={nextMonth} data-testid="next-month"><ChevronRight className="h-4 w-4" /></Button>
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                <Card className="rounded-none border-slate-200" data-testid="stat-present"><CardContent className="p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Present</p>
                    <p className="text-2xl font-black text-green-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.days_present}</p>
                    <p className="text-[10px] text-slate-400">of {daysInMonth} days</p>
                </CardContent></Card>
                <Card className="rounded-none border-slate-200" data-testid="stat-absent"><CardContent className="p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Absent</p>
                    <p className="text-2xl font-black text-red-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{Math.max(0, s.days_absent)}</p>
                    <p className="text-[10px] text-slate-400">days</p>
                </CardContent></Card>
                <Card className="rounded-none border-slate-200" data-testid="stat-leave"><CardContent className="p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">On Leave</p>
                    <p className="text-2xl font-black text-purple-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.days_on_leave}</p>
                    <p className="text-[10px] text-slate-400">days</p>
                </CardContent></Card>
                <Card className="rounded-none border-slate-200" data-testid="stat-avg-hours"><CardContent className="p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Avg Hours</p>
                    <p className="text-2xl font-black text-brand mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.avg_hours}</p>
                    <p className="text-[10px] text-slate-400">per day</p>
                </CardContent></Card>
                <Card className="rounded-none border-slate-200" data-testid="stat-punctuality"><CardContent className="p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Punctuality</p>
                    <p className={`text-2xl font-black mt-1 ${s.punctuality_score >= 80 ? 'text-green-600' : s.punctuality_score >= 50 ? 'text-amber-600' : 'text-red-600'}`} style={{ fontFamily: 'Chivo, sans-serif' }}>{s.punctuality_score}%</p>
                    <p className="text-[10px] text-slate-400">on-time rate</p>
                </CardContent></Card>
                <Card className="rounded-none border-slate-200" data-testid="stat-late"><CardContent className="p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Late Arrivals</p>
                    <p className="text-2xl font-black text-amber-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.late_days}</p>
                    <p className="text-[10px] text-slate-400">days</p>
                </CardContent></Card>
            </div>

            {/* Heatmap + Pie */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Monthly Heatmap */}
                <Card className="rounded-none border-slate-200 lg:col-span-2" data-testid="attendance-heatmap">
                    <CardHeader className="border-b border-slate-200 py-3">
                        <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <Calendar className="h-4 w-4 text-brand" />Monthly Attendance Heatmap
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                        <div className="grid grid-cols-7 gap-1 mb-2">
                            {DAY_NAMES.map(d => <div key={d} className="text-center text-[10px] font-bold text-slate-400 uppercase">{d}</div>)}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                            {heatmapCells.map((day, i) => {
                                if (!day) return <div key={`empty-${i}`} className="aspect-square" />;
                                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const dayData = data.heatmap[dateStr];
                                const today = new Date();
                                const isToday = day === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();
                                return (
                                    <div key={`day-${day}`} className={`aspect-square flex flex-col items-center justify-center ${getCellColor(dayData)} ${isToday ? 'ring-2 ring-blue-600' : ''} transition-colors cursor-pointer hover:opacity-80`}
                                        title={getCellTooltip(dayData, day)} data-testid={`heatmap-day-${day}`}>
                                        <span className={`text-xs font-medium ${isToday ? 'text-blue-700' : 'text-slate-700'}`}>{day}</span>
                                        {dayData && dayData.hours > 0 && <span className="text-[8px] text-slate-500">{dayData.hours}h</span>}
                                        {dayData?.is_late && <span className="text-[7px] text-amber-700 font-bold">L</span>}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex flex-wrap gap-4 mt-4 pt-3 border-t border-slate-100 text-[10px]">
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-200" />Present</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-200" />Late</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-purple-200" />Leave</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100" />Absent</span>
                            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-slate-100" />No Record</span>
                        </div>
                    </CardContent>
                </Card>

                {/* Attendance Distribution Pie */}
                <Card className="rounded-none border-slate-200" data-testid="attendance-pie">
                    <CardHeader className="border-b border-slate-200 py-3">
                        <CardTitle className="text-sm font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Distribution</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                        {pieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                                        {pieData.map((entry) => <Cell key={entry.name} fill={entry.fill} />)}
                                    </Pie>
                                    <Tooltip contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 0, fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : <div className="flex items-center justify-center h-[200px] text-sm text-slate-400">No data</div>}
                        <div className="space-y-2 mt-2">
                            {pieData.map(d => (
                                <div key={d.name} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: d.fill }} />
                                        <span className="text-slate-600">{d.name}</span>
                                    </div>
                                    <span className="font-bold text-slate-900">{d.value}</span>
                                </div>
                            ))}
                        </div>

                        {Object.keys(data.leave_types).length > 0 && (
                            <div className="mt-4 pt-3 border-t border-slate-100">
                                <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">Leave Breakdown</p>
                                {Object.entries(data.leave_types).map(([type, count]) => (
                                    <div key={type} className="flex items-center justify-between text-sm py-1">
                                        <span className="text-slate-600 capitalize">{type}</span>
                                        <Badge className="rounded-none bg-purple-100 text-purple-700 text-[10px]">{count}d</Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Weekly Hours Chart */}
            {data.weekly_hours.length > 0 && (
                <Card className="rounded-none border-slate-200" data-testid="weekly-hours-chart">
                    <CardHeader className="border-b border-slate-200 py-3">
                        <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <TrendingUp className="h-4 w-4 text-brand" />Weekly Hours
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={data.weekly_hours}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="week" tick={{ fontSize: 11 }} stroke="#64748b" />
                                <YAxis tick={{ fontSize: 11 }} stroke="#64748b" />
                                <Tooltip contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 0, fontSize: 12 }} />
                                <Bar dataKey="hours" fill="#2563eb" name="Hours" />
                            </BarChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            )}

            {/* Punctuality Breakdown */}
            <Card className="rounded-none border-slate-200" data-testid="punctuality-breakdown">
                <CardHeader className="border-b border-slate-200 py-3">
                    <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <Zap className="h-4 w-4 text-amber-500" />Punctuality Breakdown
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                    <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-green-50 border border-green-200">
                            <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                            <p className="text-2xl font-black text-green-700" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.on_time_days}</p>
                            <p className="text-xs text-green-600 mt-1">On Time</p>
                        </div>
                        <div className="text-center p-4 bg-amber-50 border border-amber-200">
                            <AlertCircle className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                            <p className="text-2xl font-black text-amber-700" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.late_days}</p>
                            <p className="text-xs text-amber-600 mt-1">Late Arrivals</p>
                        </div>
                        <div className="text-center p-4 bg-orange-50 border border-orange-200">
                            <Timer className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                            <p className="text-2xl font-black text-orange-700" style={{ fontFamily: 'Chivo, sans-serif' }}>{s.early_checkout_days}</p>
                            <p className="text-xs text-orange-600 mt-1">Early Checkouts</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AttendanceAnalytics;
